import { useEffect, useState } from "react";
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Copy,
  Check,
  Info,
  Link2,
  Trash2,
  Edit3,
  Plus,
  Play,
  Clock,
  Save,
  X as XIcon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Toaster, toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  getHealth,
  getSavedSheets,
  createSavedSheet,
  updateSavedSheet,
  deleteSavedSheet,
  runSheetNow,
  switchTwilioMode,
} from "../lib/api";

export default function Settings() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheets, setSheets] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([getHealth(), getSavedSheets()]);
      setHealth(h);
      setSheets(s.sheets || []);
    } catch {
      setHealth({ ok: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6 animate-fade-in">
      <Toaster position="top-center" richColors dir="rtl" />

      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">הגדרות</h1>
        <p className="text-muted-foreground mt-1">ניהול גיליונות, אינטגרציות ותזמונים</p>
      </div>

      {/* Saved Sheets Manager */}
      <SavedSheetsSection sheets={sheets} onRefresh={load} />

      {/* Instructions */}
      <div className="bg-brand-50 border border-brand/20 rounded-xl p-5" data-testid="settings-instructions">
        <div className="flex gap-3">
          <Info className="h-5 w-5 text-brand shrink-0 mt-0.5" />
          <div className="text-sm text-brand-700 leading-relaxed">
            <p className="font-semibold mb-2">🔔 הערה חשובה לגבי WhatsApp (Twilio Sandbox):</p>
            <ol className="list-decimal pr-4 space-y-1.5">
              <li>
                כל לקוח שמקבל הודעה צריך לשלוח פעם אחת למספר{" "}
                <code className="bg-white px-1.5 py-0.5 rounded border border-brand/20 font-mono text-xs">
                  +14155238886
                </code>{" "}
                את הטקסט <code className="bg-white px-1.5 py-0.5 rounded border border-brand/20 font-mono text-xs">join discuss-anyone</code>
              </li>
              <li>מגבלת Sandbox: עד 5 לקוחות מאומתים</li>
              <li>בפרודקשן: נרשם WhatsApp Business Number עם Meta</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Integration Status */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold">סטטוס אינטגרציות</h2>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-status-button">
          <RefreshCw className={`h-4 w-4 ml-2 ${loading ? "animate-spin" : ""}`} />
          רענן
        </Button>
      </div>

      {/* Twilio Mode Toggle */}
      <TwilioModeToggle health={health} onRefresh={load} />

      <IntegrationCard
        name="חשבונית ירוקה"
        description="הפקת חשבונות עסקה אוטומטיים (type 300)"
        status={health?.green_invoice}
        testId="greeninvoice-status"
      />

      <IntegrationCard
        name="WhatsApp (Twilio)"
        description="שליחת הודעות וואטסאפ עם מסמכים"
        status={health?.twilio}
        testId="twilio-status"
      />
    </div>
  );
}

function TwilioModeToggle({ health, onRefresh }) {
  const [switching, setSwitching] = useState(false);
  const isProd = health?.twilio_balance?.is_production;
  const fromNum = health?.twilio_balance?.from_number;

  const handleSwitch = async (toMode) => {
    setSwitching(true);
    try {
      const res = await switchTwilioMode(toMode);
      toast.success(
        toMode === "sandbox" ? "🧪 עברת למצב טסט (Sandbox)" : "🚀 עברת למצב פרודקשן",
        { description: `מספר שולח: ${res.from_number}` }
      );
      await onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה במעבר מצב");
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="bg-white border-2 border-amber-300 rounded-2xl p-5" data-testid="twilio-mode-toggle">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{isProd ? "🚀" : "🧪"}</span>
            <h3 className="font-heading font-bold text-stone-900">
              מצב שליחת WhatsApp: {isProd ? "פרודקשן" : "טסט (Sandbox)"}
            </h3>
          </div>
          <p className="text-xs text-stone-600 leading-relaxed">
            {isProd ? (
              <>
                <strong>פרודקשן:</strong> שליחה ללא הגבלה - הלקוחות לא צריכים להירשם.
                <br />
                שולח ממספר <code className="bg-stone-100 px-1 py-0.5 rounded font-mono text-xs" dir="ltr">{fromNum}</code>
                {" "}- דורש תבנית מאושרת מ-Meta.
              </>
            ) : (
              <>
                <strong>Sandbox:</strong> מצב טסט - רק לקוחות שעשו <code className="bg-stone-100 px-1 py-0.5 rounded font-mono text-xs">join discuss-anyone</code> יקבלו.
                <br />
                שולח ממספר <code className="bg-stone-100 px-1 py-0.5 rounded font-mono text-xs" dir="ltr">{fromNum}</code>
                {" "}- אידיאלי לבדיקות.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isProd ? (
            <Button
              onClick={() => handleSwitch("sandbox")}
              disabled={switching}
              variant="outline"
              size="sm"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              data-testid="switch-to-sandbox"
            >
              🧪 עבור למצב טסט
            </Button>
          ) : (
            <Button
              onClick={() => handleSwitch("production")}
              disabled={switching}
              size="sm"
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="switch-to-production"
            >
              🚀 עבור לפרודקשן
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SavedSheetsSection({ sheets, onRefresh }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // sheet being edited
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [running, setRunning] = useState(null);

  const handleSave = async (data, id) => {
    try {
      if (id) await updateSavedSheet(id, data);
      else await createSavedSheet(data);
      toast.success(id ? "הגיליון עודכן" : "הגיליון נשמר");
      setShowAdd(false);
      setEditing(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteSavedSheet(id);
      toast.success("הגיליון נמחק");
      setDeleteConfirm(null);
      onRefresh();
    } catch {
      toast.error("שגיאה במחיקה");
    }
  };

  const handleRun = async (id) => {
    setRunning(id);
    try {
      const res = await runSheetNow(id);
      if (res.ok) {
        toast.success(
          `נשלחו ${res.success || 0}/${res.total || 0} הודעות`,
          { description: res.failed ? `${res.failed} נכשלו` : "הכל עבר בהצלחה" }
        );
      } else {
        toast.error(res.error || "הפעלה נכשלה");
      }
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בהפעלה");
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="saved-sheets-section">
        <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
              <Link2 className="h-5 w-5 text-brand" />
              גיליונות Google Sheets שמורים
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              הגדירי פעם אחת - המערכת תשלח אוטומטית בתאריך שתבחרי
            </p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="bg-brand hover:bg-brand-hover text-white" data-testid="settings-add-sheet">
            <Plus className="h-4 w-4 ml-2" />
            הוסף גיליון
          </Button>
        </div>

        {sheets.length === 0 ? (
          <div className="p-10 text-center">
            <Link2 className="h-10 w-10 mx-auto text-stone-300 mb-2" />
            <p className="text-sm text-stone-500">אין עדיין גיליונות שמורים</p>
            <p className="text-xs text-stone-400 mt-1">הוסיפי את הגיליון הראשון שלך כדי להתחיל</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sheets.map((sheet) => (
              <div key={sheet.id} className="p-5" data-testid={`settings-sheet-${sheet.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-stone-900">{sheet.name}</h3>
                      {!sheet.active && (
                        <span className="text-[10px] bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full">לא פעיל</span>
                      )}
                    </div>
                    <p className="text-xs font-mono text-stone-500 truncate mt-1" dir="ltr">
                      {(sheet.url || "").replace(/^https?:\/\//, "")}
                    </p>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {sheet.schedule_day ? (
                        <span className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 text-xs px-2 py-1 rounded-full">
                          <Clock className="h-3 w-3" />
                          שליחה אוטומטית: יום {sheet.schedule_day} בחודש, {sheet.schedule_time}
                        </span>
                      ) : (
                        <span className="text-xs text-stone-400">ללא תזמון (ידני בלבד)</span>
                      )}
                      {sheet.last_run_date && (
                        <span className="text-xs text-stone-500">
                          • נשלח לאחרונה: {new Date(sheet.last_run_date).toLocaleDateString("he-IL")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRun(sheet.id)}
                      disabled={running === sheet.id}
                      data-testid={`run-sheet-${sheet.id}`}
                      className="gap-1"
                    >
                      {running === sheet.id ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      הרץ עכשיו
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditing(sheet)}
                      data-testid={`edit-sheet-${sheet.id}`}
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setDeleteConfirm(sheet)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-sheet-${sheet.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <SheetEditDialog
        open={showAdd || editing !== null}
        sheet={editing}
        onClose={() => {
          setShowAdd(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      {/* Delete Confirm */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-right">מחיקת גיליון</DialogTitle>
            <DialogDescription className="text-right">
              האם למחוק את "{deleteConfirm?.name}"? כל התזמונים יבוטלו.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-sheet">
              <Trash2 className="h-4 w-4 ml-2" />
              מחק
            </Button>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              ביטול
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SheetEditDialog({ open, sheet, onClose, onSave }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [day, setDay] = useState("");
  const [time, setTime] = useState("10:00");
  const [active, setActive] = useState(true);

  useEffect(() => {
    if (sheet) {
      setName(sheet.name || "");
      setUrl(sheet.url || "");
      setDay(sheet.schedule_day ? String(sheet.schedule_day) : "");
      setTime(sheet.schedule_time || "10:00");
      setActive(sheet.active !== false);
    } else if (open) {
      setName("");
      setUrl("");
      setDay("");
      setTime("10:00");
      setActive(true);
    }
  }, [sheet, open]);

  const handleSubmit = () => {
    if (!name.trim() || !url.trim()) {
      toast.error("יש למלא שם וקישור");
      return;
    }
    onSave(
      {
        name: name.trim(),
        url: url.trim(),
        schedule_day: day ? parseInt(day, 10) : null,
        schedule_time: time || "10:00",
        active,
      },
      sheet?.id
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="sm:max-w-md" data-testid="sheet-edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-right">{sheet ? "עריכת גיליון" : "הוספת גיליון חדש"}</DialogTitle>
          <DialogDescription className="text-right">
            הגדירי פרטים ותזמון אוטומטי
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <Label>שם הגיליון</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="למשל: לקוחות חודשיים"
              className="mt-1"
              data-testid="sheet-edit-name"
            />
          </div>
          <div>
            <Label>קישור Google Sheets</Label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/..."
              dir="ltr"
              className="mt-1 text-right"
              data-testid="sheet-edit-url"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>יום בחודש</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                placeholder="1-31"
                className="mt-1"
                data-testid="sheet-edit-day"
              />
            </div>
            <div>
              <Label>שעה</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-1"
                data-testid="sheet-edit-time"
              />
            </div>
          </div>
          <div className="flex items-center justify-between py-2 px-3 bg-stone-50 rounded-lg">
            <Label htmlFor="active-switch" className="text-sm cursor-pointer">
              תזמון פעיל
            </Label>
            <Switch
              id="active-switch"
              checked={active}
              onCheckedChange={setActive}
              data-testid="sheet-edit-active"
            />
          </div>
          <p className="text-xs text-stone-500 leading-relaxed">
            💡 השאירי "יום בחודש" ריק לשליחה ידנית בלבד.
            <br />
            אם מוגדר יום - המערכת תשלח אוטומטית כל חודש בתאריך הזה.
          </p>
        </div>

        <DialogFooter className="flex-row-reverse gap-2 sm:gap-2">
          <Button onClick={handleSubmit} className="bg-brand hover:bg-brand-hover text-white" data-testid="sheet-edit-save">
            <Save className="h-4 w-4 ml-2" />
            {sheet ? "עדכן" : "שמור"}
          </Button>
          <Button variant="outline" onClick={onClose}>
            ביטול
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IntegrationCard({ name, description, status, testId }) {
  const ok = status?.ok;
  return (
    <div className="bg-white rounded-xl border border-border p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-semibold">{name}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
          ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
        }`}>
          {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
          {ok ? "מחובר ופעיל" : "לא מחובר"}
        </div>
      </div>
      {!ok && status?.error && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 break-all">
          {status.error}
        </div>
      )}
    </div>
  );
}
